import { Box, createStyles, Divider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex } from "@rpi/openapi-core";
import React from "react";
import { CellLink } from "./cells/CellLink";
import { OrderItemStatusCell } from "./cells/OrderItemStatusCell";
import { tokens } from "@blurb/codex-react";
import { Body, Heading, Label } from "./Typography";
import { formatCurrency } from "../utils/misc.util";

const useStyles = createStyles({
  root: {
    gap: tokens.spacing["5"],
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  thumbnail: {
    width: 80,
    height: 80,
    borderRadius: tokens.radius.base,
    flexShrink: 0,
    backgroundColor: `${tokens.color.primitive.light.foam["50"]}`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  contentColumn: {
    gap: tokens.spacing["5"],
    flex: 1,
    minWidth: 0,
  },
  detailsRow: {
    gap: tokens.spacing["4"],
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  detailColumn: {
    gap: tokens.spacing["1"],
    flex: "1 1 0",
    minWidth: 0,
  },
  assetsCard: {
    border: `1px solid ${tokens.color.semantic.bg.surfaceSunken}`,
    borderRadius: tokens.radius.base,
    padding: tokens.spacing["4"],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing["4"],
    flex: "1 1 0",
    minWidth: 0,
  },
  detailRowWrap: {
    flexWrap: "wrap",
  },
  filesColumn: {
    gap: tokens.spacing["2"],
  },
  fileRow: {
    gap: tokens.spacing["1"],
    alignItems: "center",
  },
  detailRow: {
    gap: tokens.spacing["1"],
    alignItems: "center",
  },
});

type OrderItemFromQuery = NonNullable<NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>["orderItems"]>[number];

interface OrderItemCardProps {
  orderItem: OrderItemFromQuery;
}

const getSkuThumbnailUrl = (sku?: string | null): string | undefined => {
  if (sku?.includes("SCIW")) return "/assets/images/book-covers/view_SC_product_details.png";
  if (sku?.includes("HCIW")) return "/assets/images/book-covers/view_HC_product_details.png";
  return undefined;
};

export const OrderItemCard: React.FC<OrderItemCardProps> = ({ orderItem }) => {
  const { classes } = useStyles();

  const title = orderItem.customerProduct?.name
    ? `${orderItem.id} (${orderItem.customerProduct.name})`
    : `${orderItem.id}`;

  const thumbnailUrl = getSkuThumbnailUrl(orderItem.openapiProduct?.openapiSku) ?? orderItem.imageUri;
  const isPurged = orderItem.printAsset?.status === GraphQL.PrintAssetStatus.Purged;
  const coverUrl = !isPurged ? orderItem.printAsset?.preflightOutputFiles?.cover?.srcUrl : null;
  const gutsUrl = !isPurged ? orderItem.printAsset?.preflightOutputFiles?.guts?.srcUrl : null;
  const hasFiles = coverUrl || gutsUrl || isPurged;
  const hasStatus = orderItem.orderItemStatus;
  const showAssetsCard = hasFiles || hasStatus;
  const customerProduct = orderItem.customerProduct;
  const cost =
    orderItem.unitPrice != null && orderItem.quantity != null ? orderItem.unitPrice * orderItem.quantity : null;
  const amount =
    cost != null
      ? formatCurrency(GraphQL.SupportedCurrency.Usd, cost, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-";

  return (
    <Flex.Row className={classes.root}>
      <Box className={classes.thumbnail} sx={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined} />

      <Flex.Column className={classes.contentColumn}>
        <Heading>{title}</Heading>
        <Flex.Row className={classes.detailsRow}>
          <Flex.Column className={classes.detailColumn}>
            {orderItem.openapiProduct?.openapiSku && (
              <DetailRow label="Product SKU" value={orderItem.openapiProduct.openapiSku} />
            )}
            {customerProduct?.description && <DetailRow label="Description" value={customerProduct?.description} />}
            {orderItem.pagecount != null && <DetailRow label="Pages (Per Qty)" value={String(orderItem.pagecount)} />}
            {orderItem.quantity != null && <DetailRow label="Qty" value={orderItem.quantity.toLocaleString()} />}
            {amount && <DetailRow label="Amount" value={amount} />}
          </Flex.Column>

          {showAssetsCard && (
            <Box className={classes.assetsCard}>
              {hasFiles && (
                <Flex.Column className={classes.filesColumn}>
                  {(coverUrl || isPurged) && (
                    <Flex.Row className={classes.fileRow}>
                      <Label>Cover:</Label>
                      {coverUrl ? (
                        <CellLink to={coverUrl} external>
                          Cover file
                        </CellLink>
                      ) : (
                        <Body>Cover file (expired)</Body>
                      )}
                    </Flex.Row>
                  )}
                  {(gutsUrl || isPurged) && (
                    <Flex.Row className={classes.fileRow}>
                      <Label>Pages:</Label>
                      {gutsUrl ? (
                        <CellLink to={gutsUrl} external>
                          Pages file
                        </CellLink>
                      ) : (
                        <Body>Pages file (expired)</Body>
                      )}
                    </Flex.Row>
                  )}
                </Flex.Column>
              )}
              {hasFiles && hasStatus && <Divider sx={{ borderColor: tokens.color.semantic.bg.surfaceSunken }} />}
              {hasStatus && <OrderItemStatusCell status={orderItem.orderItemStatus} />}
            </Box>
          )}
        </Flex.Row>
      </Flex.Column>
    </Flex.Row>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { classes } = useStyles();
  return (
    <Flex.Row className={classes.detailRow}>
      <Label>{label}:</Label>
      <Body>{value}</Body>
    </Flex.Row>
  );
};
